import { CommonModule, DatePipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { GetReminderListDto } from "../../../../core/models/task-management/getReminderListDto";
import { LoaderService } from "../../../../core/services/loader.service";
import { TaskManagementService } from "../../../../core/services/task-management.service";
import { ConfirmDialogComponent } from "../../confirm-dialog/confirm-dialog.component";
import { DatePickerModule } from "primeng/datepicker";
import { DialogModule } from "primeng/dialog";
import { AccordionModule } from "primeng/accordion";
import { CardModule } from "primeng/card";
import { SpinnerComponent } from "../../spinner/spinner.component";
import { ButtonModule } from "primeng/button";

@UntilDestroy()
@Component({
  selector: "jni-reminders",
  templateUrl: "./reminders.component.html",
  styleUrls: ["./reminders.component.scss"],
  imports: [
    ConfirmDialogComponent,
    CommonModule,
    DatePickerModule,
    DialogModule,
    AccordionModule,
    ReactiveFormsModule,
    CardModule,
    SpinnerComponent,
    ButtonModule,
  ],
})
export class RemindersComponent implements OnInit {
  display = false;
  showSmallLoader = true;
  showUpcomingSmallLoader = true;
  reminders: GetReminderListDto[] = [];
  reminderForm: FormGroup;
  dateMin!: Date;
  dialogMessage = "Are you sure you want to proceed?";
  deleteReminderVisible = false;
  reminderId: number | null = 0;

  constructor(
    private _taskManagementService: TaskManagementService,
    private _toastr: JniToastrService,
    private _formBuilder: FormBuilder,
    private _loaderService: LoaderService,
    private _dataPipe: DatePipe
  ) {
    this.reminderForm = this._formBuilder.group({
      id: [0],
      title: ["", [Validators.required, Validators.minLength(3)]],
      reminderDate: ["", [Validators.required]],
      timeZone: [""],
    });
  }

  ngOnInit(): void {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 2);
    this.dateMin = new Date(now);
    this.getReminderList();
  }

  getReminderList() {
    this.showSmallLoader = true;
    this._taskManagementService
      .getReminderList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          this.reminders = (res.data || []).map(item => ({
            ...item,
            localReminderDate: new Date(item.reminderDate ?? "").toISOString(),
          }));
        },
        error: res => {
          this.showSmallLoader = false;
          this._toastr.error("Error", res.message || "Data not found");
        },
      });
  }

  createReminders() {
    if (this.reminderForm.valid) {
      this._loaderService.setLoaderState(true);
      const payload = this.reminderForm.getRawValue();
      payload.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      payload.reminderDate = this._dataPipe.transform(
        payload.reminderDate,
        "MM/dd/yyyy HH:mm:ss"
      );
      this._taskManagementService
        .addReminderData(payload)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            if (res.data != null) {
              this._loaderService.setLoaderState(false);
              this._toastr.success("Success", "Reminder added successfully!");
              this.getReminderList();
              this.display = false;
            } else {
              this._toastr.error("Error", res.message || "Error adding client");
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error("Error", res.message || "Error adding client");
          },
        });
    } else {
      this.reminderForm.markAllAsTouched();
    }
  }

  deleteReminder(reminderId: number) {
    this.deleteReminderVisible = true;
    this.reminderId = reminderId;
  }

  deleteReminderById(event: boolean) {
    if (event && this.reminderId) {
      this._loaderService.setLoaderState(true);
      this._taskManagementService
        .deleteUserReminderById(this.reminderId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            if (res.data != null) {
              this._loaderService.setLoaderState(false);
              this._toastr.success("Success", "Reminder deleted successfully!");
              this.getReminderList();
              this.display = false;
            } else {
              this._toastr.error("Error", res.message || "Error adding client");
            }
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastr.error("Error", res.message || "Error adding client");
          },
        });
    }
    this.deleteReminderVisible = false;
  }

  showDialog() {
    this.reminderForm.reset();
    this.display = true;
  }

  closeDialog() {
    this.display = false;
  }

  getBackgroundColor(reminderDate: Date | string | 0): string {
    if (!reminderDate) return "#dd4040";

    const reminder =
      typeof reminderDate === "string" || typeof reminderDate === "number"
        ? new Date(reminderDate)
        : reminderDate;

    const currentDate = new Date();
    const differenceInTime = reminder.getTime() - currentDate.getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));

    if (differenceInDays <= 3) {
      return "#dd4040";
    } else if (differenceInDays >= 7) {
      return "#acd5fa";
    } else {
      return "#e1acfa";
    }
  }
}
